import React, { useState, useEffect } from 'react';
import { Flex, Stack, Text, HStack } from '@chakra-ui/react';
import { SectionHeader } from '../sectionHeader/SectionHeader';
import BusinessCard from './businessCard';
import revenueSteps from '../../media/images/revenueSteps.png';
import piggyBank from '../../media/images/piggyBank.png';
import coupon from '../../media/images/coupon.png';
import ProfitAndLossTable from './profitAndLossTable';
import { useNexus } from '@spotoninc/nexus-react';
import TransactionDetailPage from './TransactionDetailPage';
import { DownloadIcon } from '@spotoninc/nexus-web-assets';
import { IconButton } from '@spotoninc/nexus-react';

const ReskinnedCost = ({
  revenue,
  cost,
  income,
  dateValue,
  value,
  locationValue,
  setLocation,
  pltableSum,
  highlightDesc,
  fromDate,
  toDate,
  periodFrom,
  periodTo,
}) => {
  const rangeFormat = revenue?.categories?.[0]?.match(/^\d{4}\s-\sP\d+$/)
    ? 'period'
    : 'date';
  const {
    theme: { tokens },
  } = useNexus();

  // State to track whether to show the highlight
  const [showHighlight, setShowHighlight] = useState(true);
  //For the transaction detail page
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Custom handler for setSelectedTransaction to handle both transaction selection and highlight clearing
  const handleSelectedTransactionChange = transaction => {
    // Check if this is a special clearHighlight request
    if (transaction && transaction.clearHighlight === true) {
      // Clear the highlight
      setShowHighlight(false);

      // If there's a section to toggle, pass that information to the ProfitAndLossTable
      if (transaction.sectionToToggle) {
        // We need to use a timeout to ensure the highlight is cleared first
        // This prevents the re-render issue that was closing the section
        setTimeout(() => {
          // We need to pass this information to the ProfitAndLossTable component
          // Since we can't directly call toggleSection, we'll use a ref to store this information
          // The ProfitAndLossTable component will check this ref on its next render
          window.lastSectionToToggle = transaction.sectionToToggle;

          // Force a re-render to ensure the ProfitAndLossTable sees the updated ref
          setShowHighlight(false);
        }, 0);
      }

      // Don't update the selectedTransaction state
      return;
    }

    // Normal transaction selection
    setSelectedTransaction(transaction);
  };

  // Reset showHighlight when highlightDesc changes and set a timeout to clear it
  useEffect(() => {
    if (highlightDesc) {
      setShowHighlight(true);

      // Automatically clear the highlight after 10 seconds
      const timer = setTimeout(() => {
        setShowHighlight(false);
      }, 10000);

      // Clean up the timer when the component unmounts or highlightDesc changes
      return () => clearTimeout(timer);
    }
  }, [highlightDesc]);

  if (!!selectedTransaction) {
    // Use the date range parameters from the selected transaction if available,
    // otherwise fall back to the props
    const transactionFromDate = selectedTransaction.fromDate || fromDate;
    const transactionToDate = selectedTransaction.toDate || toDate;
    const transactionPeriodFrom = selectedTransaction.periodFrom || periodFrom;
    const transactionPeriodTo = selectedTransaction.periodTo || periodTo;

    // Ensure we're passing the correct date format to the transaction detail page
    // The backend expects dates in MM-DD-YYYY format for non-period ranges
    const formatDateIfNeeded = dateStr => {
      if (!dateStr) return dateStr;

      // If it's already in MM-DD-YYYY format, return it
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        return dateStr;
      }

      // If it's in ISO format (YYYY-MM-DD), convert it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${month}-${day}-${year}`;
      }

      // If it's a date object or ISO string with time
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}-${day}-${year}`;
        }
      } catch (e) {
        console.error('Error formatting date:', e);
      }

      return dateStr;
    };

    // Format dates only if we're not using period ranges
    const formattedFromDate =
      rangeFormat === 'period'
        ? transactionFromDate
        : formatDateIfNeeded(transactionFromDate);
    const formattedToDate =
      rangeFormat === 'period'
        ? transactionToDate
        : formatDateIfNeeded(transactionToDate);

    return (
      <TransactionDetailPage
        accountKey={selectedTransaction.account_key || selectedTransaction.name}
        rangeFormat={rangeFormat}
        periodFrom={transactionPeriodFrom}
        periodTo={transactionPeriodTo}
        location={locationValue}
        setSelectedTransaction={setSelectedTransaction}
        fromDate={formattedFromDate}
        toDate={formattedToDate}
      />
    );
  }

  // Function to transform pltableSum data into the format expected by ProfitAndLossTable
  const transformPlTableData = () => {
    if (!pltableSum || !pltableSum.length) {
      return [];
    }

    // Create a structured format that preserves parent-child relationships
    const tableData = [];

    // Recursive function to process nested children
    const processChildren = (children, parentSection, level = 1) => {
      if (!children || !Array.isArray(children) || children.length === 0) {
        return;
      }

      children.forEach(child => {
        // For each child, add a special marker to indicate it belongs to the current section
        tableData.push([
          `${child.desc || ''}`, // Description
          child.subt_nat_amount_x || '0', // Current Year
          child.subt_nat_amount_y || '0', // Previous Year
          child.change || '0', // Change
          child.per_change || '0', // Change Percentage
          child.classification || '', // Classification
          parentSection, // Parent section name (hidden)
          child.account_key || '', // Account key for transaction details
          level, // Nesting level (hidden)
          child.index_ui || '', // Index UI for maintaining hierarchy (hidden)
        ]);

        // Recursively process this child's children if any
        if (
          child.children &&
          Array.isArray(child.children) &&
          child.children.length > 0
        ) {
          // If this child has its own children, it becomes a parent section for them
          processChildren(child.children, child.desc || '', level + 1);
        }
      });
    };

    // Process each top-level section
    pltableSum.forEach(section => {
      // Determine the classification for the section
      let sectionClassification = section.classification || '';

      // If no classification but name suggests a type, set a default
      if (!sectionClassification || sectionClassification === '-') {
        const sectionName = section.desc || '';
        if (/revenue|income|sales/i.test(sectionName)) {
          sectionClassification = 'Revenue';
        } else if (/expense|cost|expenditure|prime cost/i.test(sectionName)) {
          sectionClassification = 'Expense';
        }
      }

      // Add the section header first
      tableData.push([
        section.desc || '', // Description
        section.subt_nat_amount_x || '0', // Current Year
        section.subt_nat_amount_y || '0', // Previous Year
        section.change || '0', // Change
        section.per_change || '0', // Change Percentage
        sectionClassification, // Classification with proper value
        null, // No parent for top-level sections
        section.account_key || '', // Account key for transaction details
        0, // Level 0 for top-level sections
        section.index_ui || '', // Index UI for maintaining hierarchy
      ]);

      // Process all children recursively
      processChildren(section.children, section.desc || '');
    });

    return tableData;
  };

  // Get transformed data from pltableSum
  const enrichedTableData = transformPlTableData();

  const handleDownload = () => {
    if (!enrichedTableData || !enrichedTableData.length) return;

    // Prepare headers
    const headers = [
      'Description',
      'Current Year',
      'Previous Year',
      'Change',
      'Change %',
      'Classification',
    ];

    // Prepare data rows
    const rows = [];
    const processItem = item => {
      // Skip if item is undefined or doesn't have required properties
      if (!item || !item[0]) return;

      const [
        name,
        currentYear,
        previousYear,
        change,
        changePercent,
        classification,
      ] = item;

      rows.push([
        name || '',
        currentYear || '',
        previousYear || '',
        change || '',
        changePercent ? `${changePercent}%` : '',
        classification || '',
      ]);
    };

    // Process each item in enrichedTableData
    enrichedTableData.forEach(processItem);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `profit_and_loss_${fromDate || periodFrom}_to_${toDate || periodTo}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handler to clear highlight on user interaction
  const clearHighlight = () => {
    if (showHighlight) {
      setShowHighlight(false);
    }
  };

  return (
    <Flex
      direction="column"
      gap={4}
      onClick={clearHighlight}
      onKeyDown={clearHighlight}
      onScroll={clearHighlight}
      maxWidth="1920px"
      marginX="auto"
      width="100%"
      px={{ base: 4, md: 6, lg: 8 }}
    >
      <SectionHeader
        title="Cost"
        showLocationSelector={true}
        showPeriodSwitch={true}
        showDateSelector={true}
        dateValue={dateValue}
        value={value}
        onLocationChange={setLocation}
        locationValue={locationValue}
      />
      <Flex
        flexWrap="wrap"
        gap={{ base: 4, sm: 6, md: 8, lg: 12 }}
        width="100%"
        justifyContent={{ base: 'center', md: 'flex-start' }}
        alignItems="stretch"
        px={{ base: 2, sm: 4 }}
      >
        <BusinessCard
          title="Revenue"
          amount={revenue?.value?.length > 0 ? revenue?.value[0] : 0}
          image={revenueSteps}
        />
        <BusinessCard
          title="Cost"
          amount={cost?.value?.length > 0 ? cost?.value[0] : 0}
          image={coupon}
        />
        <BusinessCard
          title="Net Income"
          amount={income?.value ?? 0}
          image={piggyBank}
        />
      </Flex>
      <Stack>
        <HStack justify="space-between">
          <Text fontSize={'18px'} fontWeight={600}>
            Profit & Loss
          </Text>
          <IconButton
            icon={DownloadIcon}
            aria-label="Download"
            variant="tonal"
            label="Download"
            onClick={handleDownload}
          />
        </HStack>
        <ProfitAndLossTable
          tableData={enrichedTableData}
          setSelectedTransaction={handleSelectedTransactionChange}
          highlightEntry={showHighlight ? highlightDesc : null}
          fromDate={fromDate}
          toDate={toDate}
          periodFrom={periodFrom}
          periodTo={periodTo}
        />
      </Stack>
    </Flex>
  );
};

export default ReskinnedCost;
